<template>
  <div class="app">
    <DefaultHeader :onClickLogo="openDrawer" />

    <div class="app-body app-content">
      <v-navigation-drawer class="nav-left" fixed :mini-variant="miniVariant" v-model="drawer">
        <v-list class="nav-list" mandatory nav>
          <v-list-item-group v-model="group" active-class="blue--text current-nav">
            <v-list-item v-for="(item, i) in nav" :key="item.key" :value="item" class="nav-menu-item" :to="item.url"
              @click="onClickItemNav(item, i)">

              <template v-slot:default>
                <v-list-item-icon class="nav-icon">
                  <v-img v-if="itemSelectedIndex === i" :src="item.iconActive" width="20" height="20"></v-img>
                  <v-img v-else :src="item.icon" width="20" height="20"></v-img>
                </v-list-item-icon>
                <v-list-item-content class="nav-content">
                  <v-list-item-title class="nav-title">{{ $t(item.key) }}</v-list-item-title>
                </v-list-item-content>
              </template>
            </v-list-item>
          </v-list-item-group>
        </v-list>
      </v-navigation-drawer>

      <v-main :class="getMainClass()">
        <v-container fluid class="main-container">
          <router-view />
        </v-container>
      </v-main>
    </div>

    <DefaultFooter />
  </div>
</template>

<script>

import { getAccountPermission, NAV } from "../helper/functions";
import { filterNavByPermissions } from "../helper/nav-permissions";

import DefaultHeader from "./DefaultHeader";
import DefaultFooter from "./DefaultFooter";
import routes from "../routes";
import { mapState } from "vuex";

export default {
  name: "DefaultContainer",
  components: {
    DefaultFooter,
    DefaultHeader
  },
  data() {
    return {
      itemSelectedIndex: 0,
      drawer: true,
      miniVariant: false,
      group: null,
    };
  },
  computed: {
    ...mapState(['accountInfo', 'userPermissions']),
    nav() {
      // Convert userPermissions to array if needed
      const userPermissionsArray = Array.isArray(this.userPermissions) ? this.userPermissions : []
      
      // For employees with dynamic roles, use only new permission system
      if (this.accountInfo?.employeeId) {
        if (userPermissionsArray && userPermissionsArray.length > 0) {
          return filterNavByPermissions(NAV, userPermissionsArray)
        } else {
          return []
        }
      }
      
      // For non-employees, first filter by old permission system
      let permission = getAccountPermission(this.accountInfo)
      let filteredNav = permission ? NAV.filter(e => permission[e.functionId]) : []
      
      // Then also apply new permission system if userPermissions exist
      if (userPermissionsArray && userPermissionsArray.length > 0) {
        filteredNav = filterNavByPermissions(filteredNav, userPermissionsArray)
      }
      
      return filteredNav
    }
  },
  methods: {
    openDrawer() {
      const isMobileDevice = screen.width <= 768
      if (isMobileDevice) {
        // console.log('is Mobile')
        this.drawer = !this.drawer
      } else {
        // console.log('is Desktop')
        if (!this.drawer) this.drawer = true
        this.miniVariant = !this.miniVariant
      }
      // console.log('miniVariant', this.miniVariant)
      // console.log('drawer', this.miniVariant)
    },
    getMainClass() {
      return this.drawer ? (this.miniVariant ? 'v-main-collapse' : 'v-main-expand') : ''
    },
    onClickItemNav(item, index) {
      // console.log("onClickItemNav", item.key)
      this.itemSelectedIndex = index
    },
  },
  async created() {
    // Load user permissions if not already loaded
    if (!this.userPermissions || this.userPermissions.length === 0) {
      await this.$store.dispatch('loadUserPermissions');
    }
    
    let fullPath = routes.history.current.fullPath
    this.itemSelectedIndex = this.nav.findIndex((item) => item.url === fullPath);
  },
};
</script>
<style>
.v-main {
  background: #F0F2F5;
}

.v-main-expand {
  margin-left: 256px;
}

.v-main-collapse {
  margin-left: 56px;
}

/* Main Content Container */
.main-container {
  padding: 24px !important;
  max-width: 100% !important;
}

/* Responsive adjustments for main content */
@media (max-width: 768px) {
  .v-main-expand, .v-main-collapse {
    margin-left: 0 !important;
  }
  
  .main-container {
    padding: 16px !important;
  }
}

.nav-list {
  padding-left: 0;
  padding-right: 0;
  padding-top: 12px;
  overflow-y: auto;
  max-height: calc(100vh - 56px);
}

.current-nav {
  border-right: 4px solid #1890FF;
  background-color: rgba(24, 144, 255, 0.1) !important;
}

.app-content {
  margin-top: 56px;
}

.nav-left {
  z-index: 2;
  margin-top: 56px;
}

/* Menu Item Styling */
.nav-menu-item {
  height: 48px !important;
  min-height: 48px !important;
  padding: 8px 16px !important;
  margin-bottom: 4px;
  border-radius: 0 !important;
  margin-right: 8px;
}

.nav-menu-item:hover {
  background-color: rgba(24, 144, 255, 0.05) !important;
}

.nav-menu-item.v-list-item--active {
  background-color: rgba(24, 144, 255, 0.1) !important;
}

/* Icon Styling */
.nav-icon {
  margin-right: 12px !important;
  min-width: 20px !important;
  width: 20px !important;
  align-self: center !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

.nav-icon .v-img {
  width: 20px !important;
  height: 20px !important;
}

/* Content and Title Styling */
.nav-content {
  display: flex !important;
  align-items: center !important;
  margin: 0 !important;
}

.nav-title {
  font-size: 14px !important;
  font-weight: 500 !important;
  color: #2C3E50 !important;
  line-height: 1.2 !important;
  margin: 0 !important;
  display: flex !important;
  align-items: center !important;
}

.nav-menu-item.v-list-item--active .nav-title {
  color: #1890FF !important;
  font-weight: 600 !important;
}

/* Mini variant adjustments */
.v-navigation-drawer--mini-variant .nav-menu-item {
  justify-content: center;
  padding: 8px !important;
}

.v-navigation-drawer--mini-variant .nav-icon {
  margin-right: 0 !important;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .nav-menu-item {
    height: 44px !important;
    padding: 6px 12px !important;
  }
  
  .nav-icon {
    min-width: 18px !important;
    width: 18px !important;
  }
  
  .nav-icon .v-img {
    width: 18px !important;
    height: 18px !important;
  }
  
  .nav-title {
    font-size: 13px !important;
  }
}
</style>
